import type { ComponentPropsWithRef } from "react";
import { forwardRef } from "react";
import { omit } from "lodash";
import { FieldElement } from "@/react/components/FormRenderer/FormContent/ElementTypes/FieldElement";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { FieldElement as FieldElementType } from "@/react/types/Element";
import { useBlocksContext } from "../../contexts/BlocksContext";

interface ShortAnswerBlockMeta {
  elementableId: number;
  elementId: number;
  fieldIndex: number;
  position: number;
}
export interface FormShortAnswerBlockProps
  extends ComponentPropsWithRef<"div"> {
  blockInstanceMeta?: ShortAnswerBlockMeta;
  hidden?: boolean;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

export const FormShortAnswerBlock = forwardRef<
  HTMLDivElement,
  FormShortAnswerBlockProps
>(
  (
    {
      label = "Short answer",
      placeholder = "",
      required = false,
      hidden = false,
      className,
      blockInstanceMeta = {},
      ...rest
    },
    ref,
  ) => {
    const elementProps = omit(rest, ["blockPath"]);
    const { useFormRenderingContext } = useBlocksContext();
    const context = useFormRenderingContext();

    const element: FieldElementType = {
      id: blockInstanceMeta.elementId ?? 0,
      elementable_id: blockInstanceMeta.elementableId ?? 0,
      position: blockInstanceMeta.position ?? 0,
      type: "field",
      attributes: {
        label,
        placeholder,
        required,
        hidden,
        field_type: "short_answer",
      },
    };

    const shouldFocus =
      "isModal" in context &&
      "initialFocusRef" in context &&
      context.isModal &&
      blockInstanceMeta.fieldIndex === 0;

    const errors =
      "backendErrors" in context ? (context.backendErrors ?? {}) : {};

    return (
      <div
        ref={ref}
        className={classNames("w-full", className)}
        {...elementProps}
      >
        <FieldElement
          element={element}
          fieldIndex={blockInstanceMeta.fieldIndex ?? 0}
          errors={errors}
          initialFocusRef={
            shouldFocus && "initialFocusRef" in context
              ? context.initialFocusRef
              : undefined
          }
        />
      </div>
    );
  },
);

FormShortAnswerBlock.displayName = "FormShortAnswerBlock";
